import React from "react";
import { useParams, Link } from "react-router-dom";
import { Share2, Heart, Check, Truck, Shield, Star, Facebook, Bell, MessageCircle, Send, Mail, Link2, X as IconX, ChevronLeft, ChevronRight, FileText, Download, PlayCircle, BookOpen } from "lucide-react";
import api, { MEDIA } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProductPage() {
    const { slug } = useParams();
    const [product, setProduct] = React.useState(null);
    const [cats, setCats] = React.useState([]);
    const [activeImg, setActiveImg] = React.useState(0);
    const [qty, setQty] = React.useState(1);
    const [reviewOpen, setReviewOpen] = React.useState(false);
    const [reviewForm, setReviewForm] = React.useState({ rating: 5, title: "", comment: "", author_name: "" });
    const [canReview, setCanReview] = React.useState(null); // null=loading, {can_review, already_reviewed}
    const [wishlisted, setWishlisted] = React.useState(false);
    const [showStockAlert, setShowStockAlert] = React.useState(false);
    const [stockEmail, setStockEmail] = React.useState("");
    const [lightbox, setLightbox] = React.useState(false);
    const { addItem } = useCart();
    const { user } = useAuth();
    const { settings } = useSettings();

    React.useEffect(() => {
        api.get(`/products/${slug}`).then(r => setProduct(r.data)).catch(() => setProduct(null));
        api.get("/categories").then(r => setCats(r.data || [])).catch(() => {});
    }, [slug]);

    React.useEffect(() => {
        if (user && product) {
            api.get("/wishlist").then(r => setWishlisted((r.data?.product_ids || []).includes(product.product_id))).catch(() => {});
            api.get("/reviews/can-review", { params: { product_id: product.product_id } })
                .then(r => setCanReview(r.data))
                .catch(() => setCanReview({ can_review: false, already_reviewed: false }));
        }
        if (user?.email) setStockEmail(user.email);
    }, [user, product]);

    // Set initial active image to thumbnail_index
    React.useEffect(() => {
        if (product) setActiveImg(product.thumbnail_index ?? 0);
    }, [product]);

    // Dynamic OG / Twitter meta tags
    React.useEffect(() => {
        if (!product) return;
        const siteName = settings?.site_name || settings?.company_name || "ccashop";
        const title = `${product.name} | ${siteName}`;
        const desc = product.short_description || product.seo_description || (product.description || "").replace(/<[^>]+>/g, "").slice(0, 160);
        const p = product.sale_price || product.price || 0;
        const imgRaw = (product.images || [])[product.thumbnail_index ?? 0] || "";
        const imgUrl = imgRaw ? (imgRaw.startsWith("http") ? imgRaw : `${window.location.origin}/api/uploads/${imgRaw.replace(/^\/+/, "")}`) : "";
        const url = `${window.location.origin}/produs/${product.slug}`;
        document.title = title;
        const setMeta = (prop, val, attr = "property") => {
            let el = document.querySelector(`meta[${attr}="${prop}"]`);
            if (!el) { el = document.createElement("meta"); el.setAttribute(attr, prop); document.head.appendChild(el); }
            el.setAttribute("content", val);
        };
        setMeta("og:type", "product");
        setMeta("og:title", title);
        setMeta("og:description", desc);
        setMeta("og:url", url);
        setMeta("og:site_name", siteName);
        setMeta("product:price:amount", String(Number(p).toFixed(2)));
        setMeta("product:price:currency", "RON");
        if (imgUrl) setMeta("og:image", imgUrl);
        setMeta("twitter:card", "summary_large_image", "name");
        setMeta("twitter:title", title, "name");
        setMeta("twitter:description", desc, "name");
        if (imgUrl) setMeta("twitter:image", imgUrl, "name");
        return () => { document.title = siteName; };
    }, [product, settings]);

    if (!product) return <div className="container-x py-20 text-center text-muted-foreground">Se încarcă...</div>;

    const price = product.sale_price || product.price;
    const onSale = product.sale_price && product.sale_price < product.price;
    const physicalStock = product.stock || 0;
    const supplierStock = product.stock_supplier || 0;
    const supplierEta = product.stock_supplier_eta_days || 0;
    const manualOOS = !!product.manual_out_of_stock;
    const supplierAvailable = !!product.supplier_order_enabled && supplierStock > 0;
    const outOfStock = manualOOS || (physicalStock <= 0 && !supplierAvailable);
    const showSupplierNotice = !manualOOS && physicalStock <= 0 && supplierAvailable;
    const refSuffix = user?.referral_code ? `?ref=${user.referral_code}` : "";
    const productUrl = `${window.location.origin}/produs/${product.slug}${refSuffix}`;
    const ogShareUrl = `${BACKEND}/api/share/product/${product.slug}${refSuffix}`;
    const shareText = `${product.name} - ${price.toFixed(2)} RON | ${(product.short_description || "").slice(0, 120)}`;
    const waNumber = (settings.whatsapp_number || "").replace(/\D/g, "");

    // Build breadcrumb path
    const catMap = Object.fromEntries(cats.map(c => [c.category_id, c]));
    const currentCat = catMap[product.category_id];
    const parentCat = currentCat?.parent_id ? catMap[currentCat.parent_id] : null;

    // Staff: show installation content?
    const isStaff = ["admin", "superadmin", "sales", "installer"].includes(user?.role);
    const hasInstallVideo = product.install_video_url;
    const hasInstallManual = product.install_manual_url;
    const showInstall = (isStaff || product.install_public) && (hasInstallVideo || hasInstallManual);

    // Documents
    const docItems = [
        { field: "doc_ce_url", label: "Certificat CE" },
        { field: "doc_manual_ro_url", label: "Manual RO" },
        { field: "doc_manual_en_url", label: "Manual EN" },
        { field: "doc_datasheet_url", label: "Fișă tehnică" },
    ].filter(d => product[d.field]);

    const toggleWish = async () => {
        if (!user) { toast.info("Autentifică-te pentru a adăuga la favorite"); return; }
        try {
            const res = await api.post("/wishlist/toggle", { product_id: product.product_id });
            const nowWished = (res.data?.product_ids || []).includes(product.product_id);
            setWishlisted(nowWished);
            toast.success(nowWished ? "Adăugat la favorite" : "Eliminat din favorite");
        } catch { toast.error("Eroare"); }
    };

    const subscribeStock = async (e) => {
        e.preventDefault();
        try {
            await api.post("/stock-alerts", { product_id: product.product_id, email: stockEmail });
            toast.success("Te anunțăm pe email când produsul revine în stoc!");
            setShowStockAlert(false);
        } catch { toast.error("Eroare la înregistrare"); }
    };

    const nativeShare = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: product.name, text: shareText, url: productUrl }); }
            catch {}
        } else {
            navigator.clipboard.writeText(productUrl);
            toast.success("Link copiat!");
        }
    };

    const addReview = async (e) => {
        e.preventDefault();
        try {
            await api.post("/reviews", { ...reviewForm, product_id: product.product_id });
            toast.success("Recenzie trimisă! Va fi publicată după aprobare.");
            setReviewOpen(false);
            setReviewForm({ rating: 5, title: "", comment: "", author_name: "" });
        } catch { toast.error("Eroare"); }
    };

    // Google Schema JSON-LD
    const schemaOrg = product.google_merchant_enabled ? {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": product.name,
        "description": product.short_description || product.description || "",
        "sku": product.code,
        "brand": { "@type": "Brand", "name": product.brand || settings?.company_name || "" },
        "image": (product.images || []).map(img => MEDIA(img)),
        "offers": {
            "@type": "Offer",
            "url": productUrl,
            "priceCurrency": "RON",
            "price": price.toFixed(2),
            "availability": outOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            "itemCondition": "https://schema.org/NewCondition",
            "seller": { "@type": "Organization", "name": settings?.company_name || "" }
        },
        ...(product.reviews?.length ? {
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": (product.reviews.reduce((s, r) => s + r.rating, 0) / product.reviews.length).toFixed(1),
                "reviewCount": product.reviews.length
            }
        } : {})
    } : null;

    return (
        <div className="container-x py-10" data-testid="product-page">
            {/* JSON-LD Schema */}
            {schemaOrg && (
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }} />
            )}

            {/* Breadcrumb */}
            <nav className="text-sm text-muted-foreground mb-6 flex items-center gap-1.5 flex-wrap">
                <Link to="/magazin" className="hover:text-foreground transition-colors">Magazin</Link>
                {parentCat && (
                    <>
                        <span>/</span>
                        <Link to={`/magazin/${parentCat.slug}`} className="hover:text-foreground transition-colors">{parentCat.name}</Link>
                    </>
                )}
                {currentCat && (
                    <>
                        <span>/</span>
                        <Link to={`/magazin/${currentCat.slug}`} className="hover:text-foreground transition-colors">{currentCat.name}</Link>
                    </>
                )}
                <span>/</span>
                <span className="text-foreground font-medium truncate max-w-[200px]">{product.name}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Images */}
                <div>
                    <div
                        className="bg-muted rounded-2xl overflow-hidden mb-4 cursor-zoom-in group relative flex items-center justify-center"
                        style={{ minHeight: "300px" }}
                        onClick={() => product.images?.length && setLightbox(true)}
                        data-testid="product-main-image"
                    >
                        {product.images?.[activeImg]
                            ? <img src={MEDIA(product.images[activeImg])} alt={product.name} className="w-full max-h-[480px] object-contain transition-transform group-hover:scale-105 p-4" />
                            : <div className="w-full h-64 flex items-center justify-center text-muted-foreground">Fără imagine</div>
                        }
                        {product.images?.length > 0 && (
                            <div className="absolute bottom-3 right-3 bg-foreground/70 text-background text-xs font-mono px-2 py-1 rounded backdrop-blur-sm pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                                Click pentru zoom
                            </div>
                        )}
                    </div>
                    {product.images?.length > 1 && (
                        <div className="grid grid-cols-5 gap-2">
                            {product.images.map((img, i) => (
                                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square bg-muted rounded-xl overflow-hidden border-2 transition-colors ${i === activeImg ? "border-primary" : "border-transparent hover:border-border"}`}>
                                    <img src={MEDIA(img)} alt="" className="w-full h-full object-contain p-1" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div>
                    <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-4" data-testid="product-title">{product.name}</h1>

                    <div className="flex items-baseline gap-3 mb-2">
                        {onSale && <div className="text-xl text-muted-foreground line-through">{product.price.toFixed(2)} RON</div>}
                        <div className="font-heading text-4xl font-bold text-primary" data-testid="product-price">{price.toFixed(2)} <span className="text-base text-foreground">RON</span></div>
                        {onSale && <div className="chip-primary">-{Math.round((1 - product.sale_price / product.price) * 100)}%</div>}
                    </div>
                    {product.prp && product.prp > price && (
                        <div className="text-xs text-muted-foreground mb-4" data-testid="product-prp">
                            PRP: <span className="line-through">{product.prp.toFixed(2)} RON</span>
                            <span className="text-primary font-medium ml-2">Economisești {(product.prp - price).toFixed(2)} RON</span>
                        </div>
                    )}

                    <p className="text-base text-muted-foreground mb-6">{product.short_description}</p>

                    <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
                        <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4 text-primary flex-shrink-0" />
                            {outOfStock ? <span>La reaprovizionare</span>
                                : showSupplierNotice ? <span>Livrare {supplierEta || 7} zile lucrătoare</span>
                                : <span>Livrare 24-48h</span>}
                        </div>
                        <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                            {product.warranty_months ? `Garanție ${product.warranty_months} luni` : "Fără garanție"}
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className={`h-4 w-4 flex-shrink-0 ${outOfStock ? "text-destructive" : "text-primary"}`} />
                            {outOfStock ? <span className="text-destructive">Stoc epuizat</span>
                                : showSupplierNotice ? <span className="text-amber-600">Stoc furnizor</span>
                                : <span>În stoc</span>}
                        </div>
                        <div className="flex items-center gap-2"><Check className="h-4 w-4 text-primary flex-shrink-0" /> Instalare disponibilă</div>
                    </div>

                    {outOfStock ? (
                        <div className="mb-6">
                            {!showStockAlert ? (
                                <button onClick={() => setShowStockAlert(true)} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="stock-alert-btn">
                                    <Bell className="h-4 w-4" /> Anunță-mă când revine în stoc
                                </button>
                            ) : (
                                <form onSubmit={subscribeStock} className="card-soft p-4 space-y-2">
                                    <div className="text-sm font-medium">Îți trimitem un email imediat ce produsul revine.</div>
                                    <input type="email" required placeholder="Email" value={stockEmail} onChange={(e) => setStockEmail(e.target.value)} className="input-soft" data-testid="stock-alert-email" />
                                    <div className="flex gap-2">
                                        <button type="submit" className="btn-primary flex-1" data-testid="stock-alert-submit">Abonează-mă</button>
                                        <button type="button" onClick={() => setShowStockAlert(false)} className="btn-outline">Renunță</button>
                                    </div>
                                </form>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-stretch gap-3 mb-6">
                            <div className="flex items-center rounded-xl border border-border bg-card">
                                <button className="px-4 py-3 hover:bg-muted rounded-l-xl" onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                                <span className="px-5 font-medium" data-testid="product-qty">{qty}</span>
                                <button className="px-4 py-3 hover:bg-muted rounded-r-xl" onClick={() => setQty(q => q + 1)}>+</button>
                            </div>
                            <button
                                onClick={() => addItem({ product_id: product.product_id, quantity: qty, unit_price: price, name: product.name, image: product.images?.[product.thumbnail_index ?? 0] || product.images?.[0] || "" })}
                                className="flex-1 btn-primary" data-testid="product-add-to-cart">
                                Adaugă în coș
                            </button>
                            <button onClick={toggleWish} className={`btn-outline !px-4 ${wishlisted ? "text-primary border-primary" : ""}`} data-testid="product-wishlist" title="Favorite">
                                <Heart className={`h-5 w-5 ${wishlisted ? "fill-current" : ""}`} />
                            </button>
                        </div>
                    )}

                    {/* Social share */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="text-xs text-muted-foreground mr-1">Distribuie:</span>
                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogShareUrl)}`} target="_blank" rel="noopener noreferrer"
                            className="btn-outline !py-2 !px-3 flex items-center gap-1" title="Facebook" data-testid="share-facebook">
                            <Facebook className="h-4 w-4" /><span className="text-xs hidden sm:inline">Facebook</span>
                        </a>
                        <a href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + ogShareUrl)}`} target="_blank" rel="noopener noreferrer"
                            className="btn-outline !py-2 !px-3 flex items-center gap-1" title="WhatsApp" data-testid="share-whatsapp">
                            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01S14.69 2 12.04 2z"/></svg>
                            <span className="text-xs hidden sm:inline">WhatsApp</span>
                        </a>
                        <a href={`mailto:?subject=${encodeURIComponent(product.name)}&body=${encodeURIComponent(shareText + "\n\n" + productUrl)}`}
                            className="btn-outline !py-2 !px-3 flex items-center gap-1" title="Email" data-testid="share-email">
                            <Mail className="h-4 w-4" /><span className="text-xs hidden sm:inline">Email</span>
                        </a>
                        <button onClick={nativeShare} className="btn-outline !py-2 !px-3 flex items-center gap-1" title="Copiază link" data-testid="share-copy">
                            <Link2 className="h-4 w-4" /><span className="text-xs hidden sm:inline">Copiază link</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Lightbox */}
            {lightbox && product.images?.length > 0 && (
                <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightbox(false)} data-testid="lightbox-overlay">
                    <button onClick={(e) => { e.stopPropagation(); setLightbox(false); }} className="absolute top-4 right-4 text-white hover:text-primary p-2" data-testid="lightbox-close">
                        <IconX className="h-7 w-7" />
                    </button>
                    {product.images.length > 1 && (
                        <>
                            <button onClick={(e) => { e.stopPropagation(); setActiveImg(i => (i - 1 + product.images.length) % product.images.length); }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-primary p-3 bg-foreground/30 rounded-full backdrop-blur-sm">
                                <ChevronLeft className="h-7 w-7" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setActiveImg(i => (i + 1) % product.images.length); }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-primary p-3 bg-foreground/30 rounded-full backdrop-blur-sm">
                                <ChevronRight className="h-7 w-7" />
                            </button>
                        </>
                    )}
                    <img src={MEDIA(product.images[activeImg])} alt={product.name}
                        className="max-w-[92vw] max-h-[88vh] object-contain rounded shadow-2xl"
                        onClick={(e) => e.stopPropagation()} />
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm font-mono bg-foreground/40 backdrop-blur-sm px-4 py-2 rounded-full">
                        {activeImg + 1} / {product.images.length}
                    </div>
                </div>
            )}

            {/* Description + Specs */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-16">
                <div>
                    <h2 className="font-heading text-2xl mb-4">Descriere</h2>
                    {product.description?.startsWith("<") ? (
                        <div className="prose prose-sm prose-neutral max-w-none text-muted-foreground leading-relaxed"
                             dangerouslySetInnerHTML={{ __html: product.description }} />
                    ) : (
                        <div className="text-muted-foreground leading-relaxed whitespace-pre-line">{product.description}</div>
                    )}
                </div>
                <div>
                    <h2 className="font-heading text-2xl mb-4">Specificații</h2>
                    <div className="card-soft divide-y divide-border overflow-hidden">
                        <div className="flex justify-between py-3 px-4 text-sm"><span className="text-muted-foreground">Cod produs</span><span className="font-medium font-mono">{product.code}</span></div>
                        {product.brand && <div className="flex justify-between py-3 px-4 text-sm"><span className="text-muted-foreground">Brand</span><span className="font-medium">{product.brand}</span></div>}
                        {(() => {
                            const schema = product.specs_schema || [];
                            const schemaMap = Object.fromEntries(schema.map(s => [s.key, s]));
                            const renderValue = (v, sch) => {
                                if (typeof v === "boolean") return v ? "Da" : "Nu";
                                if (sch?.unit && v !== "" && v != null) return `${v} ${sch.unit}`;
                                return String(v);
                            };
                            return Object.entries(product.specs || {}).filter(([_, v]) => v !== "" && v != null).map(([k, v]) => {
                                const sch = schemaMap[k];
                                const label = sch?.label || k.replace(/_/g, " ");
                                return (
                                    <div key={k} className="flex justify-between py-3 px-4 text-sm gap-3">
                                        <span className="text-muted-foreground">{label}</span>
                                        <span className="font-medium text-right">{renderValue(v, sch)}</span>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </div>
            </div>

            {/* Documents */}
            {docItems.length > 0 && (
                <div className="mt-12">
                    <h2 className="font-heading text-2xl mb-4">Documente</h2>
                    <div className="flex flex-wrap gap-3">
                        {docItems.map(({ field, label }) => (
                            <a key={field} href={MEDIA(product[field])} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-2 card-soft px-4 py-3 hover:border-primary hover:text-primary transition-colors text-sm font-medium">
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                {label}
                                <Download className="h-3.5 w-3.5 ml-1 text-muted-foreground" />
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {/* Installation (staff or public) */}
            {showInstall && (
                <div className="mt-12">
                    <h2 className="font-heading text-2xl mb-4 flex items-center gap-2">
                        Ghid instalare
                        {isStaff && !product.install_public && (
                            <span className="chip bg-primary/10 text-primary text-xs">Staff only</span>
                        )}
                    </h2>
                    <div className="flex flex-wrap gap-4">
                        {hasInstallVideo && (
                            <a href={product.install_video_url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-3 card-soft px-5 py-4 hover:border-primary hover:text-primary transition-colors font-medium">
                                <PlayCircle className="h-5 w-5 text-primary flex-shrink-0" />
                                Video instalare
                            </a>
                        )}
                        {hasInstallManual && (
                            <a href={MEDIA(product.install_manual_url)} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-3 card-soft px-5 py-4 hover:border-primary hover:text-primary transition-colors font-medium">
                                <BookOpen className="h-5 w-5 text-primary flex-shrink-0" />
                                Manual instalare
                                <Download className="h-3.5 w-3.5 ml-1 text-muted-foreground" />
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* Reviews */}
            <div className="mt-16">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="font-heading text-2xl">Recenzii ({product.reviews?.length || 0})</h2>
                        {product.reviews?.length > 0 && (() => {
                            const avg = product.reviews.reduce((s, r) => s + r.rating, 0) / product.reviews.length;
                            return (
                                <div className="flex items-center gap-1.5 mt-1">
                                    <div className="flex gap-0.5">
                                        {[1,2,3,4,5].map(s => (
                                            <Star key={s} className={`h-4 w-4 ${s <= Math.round(avg) ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                                        ))}
                                    </div>
                                    <span className="text-sm font-medium">{avg.toFixed(1)}</span>
                                    <span className="text-xs text-muted-foreground">/ 5</span>
                                </div>
                            );
                        })()}
                    </div>
                    {/* Review CTA — conditional on purchase */}
                    {!user && (
                        <span className="text-sm text-muted-foreground">
                            <a href="/login" className="text-primary hover:underline">Autentifică-te</a> pentru a lăsa o recenzie
                        </span>
                    )}
                    {user && canReview?.already_reviewed && (
                        <span className="text-sm text-muted-foreground">Ai lăsat deja o recenzie</span>
                    )}
                    {user && canReview?.can_review && !canReview?.already_reviewed && (
                        <button onClick={() => setReviewOpen(!reviewOpen)} className="btn-outline" data-testid="add-review-btn">Scrie o recenzie</button>
                    )}
                    {user && canReview && !canReview.can_review && !canReview.already_reviewed && (
                        <span className="text-sm text-muted-foreground">Poți lăsa o recenzie după ce comanzi</span>
                    )}
                </div>
                {reviewOpen && canReview?.can_review && (
                    <form onSubmit={addReview} className="card-soft p-6 mb-6 space-y-3">
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map(r => (
                                <button type="button" key={r} onClick={() => setReviewForm(f => ({ ...f, rating: r }))}>
                                    <Star className={`h-6 w-6 ${r <= reviewForm.rating ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                                </button>
                            ))}
                        </div>
                        <input required placeholder="Numele tău" value={reviewForm.author_name} onChange={(e) => setReviewForm(f => ({ ...f, author_name: e.target.value }))} className="input-soft" />
                        <input placeholder="Titlu" value={reviewForm.title} onChange={(e) => setReviewForm(f => ({ ...f, title: e.target.value }))} className="input-soft" />
                        <textarea required placeholder="Comentariu" value={reviewForm.comment} onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))} rows="4" className="input-soft" />
                        <button type="submit" className="btn-primary">Trimite recenzia</button>
                    </form>
                )}
                <div className="space-y-4">
                    {(product.reviews || []).map((r, i) => (
                        <div key={i} className="card-soft p-4">
                            <div className="flex gap-1 text-primary mb-2">{[...Array(r.rating)].map((_, j) => <Star key={j} className="h-4 w-4 fill-current" />)}</div>
                            {r.title && <div className="font-medium mb-1">{r.title}</div>}
                            <p className="text-sm text-muted-foreground mb-2">{r.comment}</p>
                            <div className="text-xs text-muted-foreground">— {r.author_name}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
